/*
 * MAISON ARPENT — les apparitions au défilement de l'accueil.
 *
 * Le contrat, posé avec la feuille de styles (`arpent.css`, section 3) :
 *
 *   · la classe `arpent-anim` sur <html> est posée par le script EN LIGNE de
 *     `layout/theme.liquid`, avant le premier rendu. C'est elle, et elle
 *     seule, qui met les éléments en opacité 0. Sans elle rien n'est masqué :
 *     un échec de chargement de ce fichier ne coûte donc aucun contenu.
 *   · le même script en ligne pose `arpent-anim-secours` au bout de 2,5 s, ce
 *     qui révèle tout. Ce fichier doit donc avoir fini son travail avant.
 *
 * Ce qu'on anime, et rien d'autre : les enfants directs du contenu de chaque
 * section, et chaque tuile d'une liste de ressources. Un seul niveau par
 * section — deux niveaux imbriqués feraient attendre l'enfant que le parent
 * soit visible, et une tuile hors écran resterait masquée.
 *
 * Le décalage se calcule par RANGÉE, pas par index : sur une grille de quatre
 * colonnes, décaler la huitième tuile de 8 × 70 ms ferait attendre une demi
 * seconde une image déjà à l'écran. On regroupe donc par ordonnée.
 */
(() => {
  const CIBLES = [
    ':is(.section-content-wrapper, .section-resource-list__content) > *',
    '.resource-list__item',
  ];

  const PAS = 70; // ms entre deux rangées
  const PLAFOND = 280; // ms — au-delà, l'attente se voit

  function armer() {
    const racine = document.getElementById('MainContent');
    if (!racine) return;

    const sections = [...racine.children].filter(
      (n, i) => i > 0 && n.classList.contains('shopify-section')
    );
    if (!sections.length) return;

    const elements = [];
    for (const section of sections) {
      for (const selecteur of CIBLES) {
        elements.push(...section.querySelectorAll(selecteur));
      }
    }
    if (!elements.length) return;

    // Le décalage : on arrondit l'ordonnée à 24 px près pour que deux tuiles
    // de la même rangée tombent dans le même seau malgré les sous-pixels.
    const rangs = new Map();
    for (const el of elements) {
      const y = Math.round((el.getBoundingClientRect().top + window.scrollY) / 24);
      if (!rangs.has(y)) rangs.set(y, rangs.size);
    }

    const observateur = new IntersectionObserver(
      (entrees) => {
        for (const entree of entrees) {
          if (!entree.isIntersecting) continue;
          observateur.unobserve(entree.target);
          entree.target.classList.add('arpent-vu');
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );

    // Les rangs sont numérotés dans l'ordre du document ; on ramène le
    // décalage à zéro à chaque section pour qu'une section vue seule ne
    // traîne pas le compteur des précédentes.
    for (const section of sections) {
      const dedans = elements.filter((el) => section.contains(el));
      const base = dedans.length
        ? Math.min(...dedans.map((el) => rangs.get(
            Math.round((el.getBoundingClientRect().top + window.scrollY) / 24)
          )))
        : 0;

      for (const el of dedans) {
        // Déjà passé : le navigateur a restauré une position de défilement, ou
        // la page s'ouvre sur une ancre. Ce qui est au-dessus de la fenêtre ne
        // croisera plus jamais l'observateur — on le révèle sans transition,
        // sinon l'utilisateur qui remonte trouve des blancs.
        if (el.getBoundingClientRect().bottom < 0) {
          el.classList.add('arpent-vu');
          continue;
        }

        const rang =
          rangs.get(Math.round((el.getBoundingClientRect().top + window.scrollY) / 24)) - base;
        el.style.setProperty('--arpent-delai', `${Math.min(rang * PAS, PLAFOND)}ms`);
        observateur.observe(el);
      }
    }

    // L'observateur est en place : on désarme le filet de secours de l'entête,
    // sinon il révélerait la page entière au bout de 2,5 s.
    if (window.arpentAnimSecours) {
      clearTimeout(window.arpentAnimSecours);
      window.arpentAnimSecours = null;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', armer, { once: true });
  } else {
    armer();
  }
})();
